import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/crm/api";

/**
 * Theme + wallpaper context.
 *
 * • `theme`: "dark" | "light"
 * • `wallpaper`: URL string (any image) or one of the built-in wallpaper ids
 *
 * All values are persisted server-side per user via /api/crm/preferences.
 */

export const WALLPAPERS = [
  {
    id: "blue-sky",
    label: "Blue Sky",
    url: "/wallpapers/blue-sky.png",
  },
  {
    id: "midnight-glass",
    label: "Midnight Glass",
    url: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=2400&q=80",
  },
  {
    id: "aurora",
    label: "Aurora",
    url: "https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&w=2400&q=80",
  },
  {
    id: "gold-mist",
    label: "Gold Mist",
    url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=2400&q=80",
  },
  {
    id: "toronto-dusk",
    label: "Toronto Dusk",
    url: "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=2400&q=80",
  },
  {
    id: "linen",
    label: "Linen",
    url: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=2400&q=80",
  },
  {
    id: "none",
    label: "Solid",
    url: null,
  },
];

const ThemeCtx = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState("dark");
  const [wallpaper, setWallpaperState] = useState(null);
  const [ready, setReady] = useState(false);

  // Load preferences after login
  useEffect(() => {
    api
      .get("/crm/preferences")
      .then((r) => {
        setThemeState(r.data.theme || "dark");
        setWallpaperState(r.data.wallpaper_url || null);
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  // Apply theme class to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("theme-dark", "theme-light");
    root.classList.add(theme === "light" ? "theme-light" : "theme-dark");
    root.dataset.theme = theme;
  }, [theme]);

  // Paint wallpaper on <body> so nothing can cover it, then restore on unmount.
  useEffect(() => {
    const body = document.body;
    const prev = body.style.cssText;
    if (wallpaper) {
      body.style.background = `url(${wallpaper}) center / cover no-repeat fixed`;
      body.style.backgroundAttachment = "fixed";
    }
    return () => {
      body.style.cssText = prev;
    };
  }, [wallpaper]);

  const setTheme = (t) => {
    setThemeState(t);
    api.patch("/crm/preferences", { theme: t }).catch(() => {});
  };
  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const setWallpaper = (url) => {
    setWallpaperState(url);
    api.patch("/crm/preferences", { wallpaper_url: url || "" }).catch(() => {});
  };

  return (
    <ThemeCtx.Provider
      value={{ theme, setTheme, toggleTheme, wallpaper, setWallpaper, ready }}
    >
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
